import type { FolderDriveItem } from '../components/Document/DocumentFlatTree'

export type FileDocumentListener = () => void

/**
 * A snapshot of the document tree as `DocumentFlatTree` had it loaded: the (partially crawled) items plus the
 * bookkeeping needed to restore it. `loadedFolderIds` cannot be derived from the items - it is the set the tree
 * uses to tell "this folder has no children" apart from "this folder was never opened", and it drives the
 * document counters. Restoring the items without it makes every folder show a "?" and re-fetch on click.
 */
export type DriveItemTreeSnapshot = {
    documentRootId: string
    items: FolderDriveItem[]
    loadedFolderIds: string[]
    rootFolder: FolderDriveItem
}

/**
 * Session cache for the documents of the dossier the user currently has open (HAM-4295). It holds two separate
 * slots, because they are not interchangeable:
 *
 * - `tree`: what `DocumentFlatTree` loaded, which is by definition incomplete (prefetch depth plus whatever the
 *   user expanded), but does contain documents.
 * - `folders`: the complete folder-only crawl behind the "Standaarddocument" combobox, which may not miss a
 *   target folder, but contains no documents.
 *
 * There is one entry, for one dossier: everything is keyed on `documentRootId` and a read for another dossier
 * returns undefined rather than the wrong dossier's documents. The cache deliberately does not outlive the visit
 * to the dossier - see `FileProvider`, which drops it on unmount, on `FileAction.ResetCurrent` and on an
 * environment switch.
 */
export type FileDocumentStore = {
    getTree(documentRootId: string | undefined): DriveItemTreeSnapshot | undefined
    setTree(snapshot: DriveItemTreeSnapshot): void
    getFolders(documentRootId: string | undefined): FolderDriveItem[] | undefined
    setFolders(documentRootId: string | undefined, folders: FolderDriveItem[] | undefined): void
    /** Drops both slots. Without an id everything is dropped, whichever dossier is cached. */
    invalidate(documentRootId?: string): void
    /** Drops only the document tree. Used when a tree reloads: the folder crawl behind it is still valid. */
    invalidateTree(documentRootId: string | undefined): void
    subscribe(listener: FileDocumentListener): () => void
}

type Entry = {
    documentRootId: string
    tree?: DriveItemTreeSnapshot
    folders?: FolderDriveItem[]
}

export function createFileDocumentStore(): FileDocumentStore {
    // A plain object rather than React state: writes come from a tree that is mid-render elsewhere, and only the
    // components that subscribe should re-render. The store itself is created once and never replaced.
    let entry: Entry | undefined
    const listeners = new Set<FileDocumentListener>()

    const notify = () => listeners.forEach(listener => listener())

    const entryFor = (documentRootId: string | undefined): Entry | undefined =>
        documentRootId && entry?.documentRootId === documentRootId ? entry : undefined

    return {
        getTree(documentRootId: string | undefined): DriveItemTreeSnapshot | undefined {
            return entryFor(documentRootId)?.tree
        },

        setTree(snapshot: DriveItemTreeSnapshot) {
            if (!snapshot?.documentRootId) {
                return
            }
            entry =
                entry?.documentRootId === snapshot.documentRootId
                    ? { ...entry, tree: snapshot }
                    : { documentRootId: snapshot.documentRootId, tree: snapshot }
            notify()
        },

        getFolders(documentRootId: string | undefined): FolderDriveItem[] | undefined {
            return entryFor(documentRootId)?.folders
        },

        setFolders(documentRootId: string | undefined, folders: FolderDriveItem[] | undefined) {
            if (!documentRootId) {
                return
            }
            entry = entry?.documentRootId === documentRootId ? { ...entry, folders } : { documentRootId, folders }
            notify()
        },

        invalidateTree(documentRootId: string | undefined) {
            if (!entryFor(documentRootId)?.tree) {
                return
            }
            entry = { ...entry, tree: undefined }
            notify()
        },

        invalidate(documentRootId?: string) {
            if (!entry || (documentRootId && entry.documentRootId !== documentRootId)) {
                return
            }
            entry = undefined
            notify()
        },

        subscribe(listener: FileDocumentListener): () => void {
            listeners.add(listener)
            return () => {
                listeners.delete(listener)
            }
        },
    }
}
